import { createApp, defineAsyncComponent } from 'vue'

import { envOverrides } from '@sennder/shell-utilities'
import '@sennder/shell-utilities/dist/style.css'

import router from '@/router'
import { loadUserData, setStoreLegacyFlags } from '@/store'
import { ENV, LAUNCH_DARKLY_KEY } from '@/common/config'
import { logger } from '@/services/logger/loggers'
import { startTracking } from '@/services/tracking'
import { translationProvider } from '@/services/translationProvider'
import { featureFlag } from '@/services/featureFlagProvider'
import { microfrontends } from './config/microfrontends'
import { initRouter } from './router/routes'
import { auth, loadAuthRemoteFunction } from '@/modules/auth'
import { initSessionReplay, stopSessionReplay } from '@/services/analytics'
import { i18n } from '@/services/i18n'
import { getCookieConsent } from '@/services/cookies'
import { themeProvider, Themes } from '@sennder/design-system-core'

async function checkCookieConsent() {
  try {
    const consent = await getCookieConsent()

    if (consent.ANALYTICS) {
      startTracking()
    }
  } catch (error) {
    logger.error(
      `[otters-shell - checkCookieConsent] Error checking cookie consent`,
      { error }
    )
  }
}

const bootPromises: Promise<void>[] = []
let userDataPromise: Promise<void> | undefined = undefined

async function start() {
  themeProvider('body', Themes.Light)

  await checkCookieConsent()

  try {
    window.onerror = function (message, url, line, column, error) {
      logger.error('[shippers - global error]', { error })
    }

    const authFnPromise = loadAuthRemoteFunction()

    if (ENV === 'dev') {
      envOverrides.load({
        legacyModules: Object.values(microfrontends),
        logger,
      })
    }

    if (!LAUNCH_DARKLY_KEY) {
      throw new Error('LAUNCH_DARKLY_KEY not found')
    }

    bootPromises.push(
      featureFlag.init(LAUNCH_DARKLY_KEY),
      translationProvider.initialize()
    )

    await authFnPromise
    if (await auth.value?.isAuthenticated()) {
      userDataPromise = loadUserData()
    }
  } catch (error) {
    logger.error(`[shippers] shell init`, { error })
  } finally {
    initRouter()

    await userDataPromise
    const app = createApp(defineAsyncComponent(() => import('./App.vue')))
      .use(router)
      .use(i18n)

    const { unmount } = app

    // This should be the only watchAllFlags call in the application to avoid noisy logging to LaunchDarkly. Otherwise, each watchAllFlags will report another set of feature flag evaluations.
    const stopWatchLegacyFlags =
      featureFlag.devFunctions.watchAllFlags(setStoreLegacyFlags)

    const stopWatchAmplitudeReplayFlag = featureFlag.watchFeatureFlag(
      'ENABLE_AMPLITUDE_SESSION_REPLAY',
      (enable: boolean) => {
        if (enable) {
          initSessionReplay()
        } else {
          stopSessionReplay()
        }
      }
    )

    app.unmount = (...args) => {
      unmount(...args)
      stopWatchAmplitudeReplayFlag()
      stopWatchLegacyFlags()
    }

    await Promise.allSettled(bootPromises)
    await router.isReady()
    app.mount('#app')
  }
}

start()
